import React, { ChangeEvent, useState, useEffect, FormEvent } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store/ducks';

import Text from '../Text';
import { Button } from '../Button';
import Input from 'components/Input';
import { Row, Column } from '../Layout/Grid';
import RadioInput from 'components/RadioInput';
import { Visibility } from 'components/Visibility';
import SwitchComponent from 'components/SwitchComponent';
import { MultiSelectCustomized } from 'components/MultiSelect/MultiSelectCustomized';
import PeriodDateToInput from 'components/PeriodDateToInput';

import {
  calcDataFrom,
  calcDataTo,
  getMaxDateInSearchFilter,
  getMinDateInSearchFilter
} from 'utils/Date';
import { DateOutputStr } from 'utils/DateOutput';

import { OrdersTabsEnum, TicketReasonEnum } from 'models/Order';
import { ISubsidiary } from 'models/Subsidiary';
import { BUSelect } from 'models/BusinessUnits';
import { EnumUserType } from 'models/User';

import arrowFilter from 'img/arrow-filter.svg';
import IconSearch from 'img/search-icon.svg';

import DomainSettingService from 'services/domainSetting.service';
import SubsidiaryService from 'services/subsidiary.service';

import {
  EnterpriseProps,
  OrderTableSearchProps,
  SituationProps,
  MultiSelectProps
} from './interface';

import { isMobileDevice } from 'utils/ScreenUtils';
import { formatDocument } from 'utils/Document';

import * as Styled from "./styles";

const TableSearchOrder: React.FC<OrderTableSearchProps> = (props) => {
  const {
    placeholder,
    className,
    setCurrentPage,
    setTableFilter,
    viewFilters,
    clearBtn = false,
    showAdvancedFilters = false,
    showTypeDateFilter = false,
    setIsLoadOrder,
    initialSearchFilter
  } = props;

  const currentTabPage = useSelector((state: RootState) => state.currentTab?.currentTabPage);
  const currentTabNamePage = useSelector((state: RootState) => state.currentTab?.currentNameTab);
  const user = useSelector((state: RootState) => state.user);

  const randomTableSearchId = `${Math.random() * 1000}`;

  const [searchStr, setSearchStr] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [period, setPeriod] = useState({
    from: getMinDateInSearchFilter() ?
      DateOutputStr(getMinDateInSearchFilter().toISOString(), 'yyyy-MM-dd') : '',
    to: getMaxDateInSearchFilter() ?
      DateOutputStr(getMaxDateInSearchFilter().toISOString(), 'yyyy-MM-dd') : '',
  });
  const [selectDateType, setSelectDateType] = useState("BillingDate");
  const [selectedSituation, setSelectedSituation] = useState<Array<string>>([]);
  const [selectedSubsidiaries, setSelectedSubsidiaries] = useState<Array<string>>([]);
  const [selectedEnterprise, setSelectedEnterprise] = useState<Array<string>>([]);

  const [allSituation, setAllSituationOption] = useState<Array<MultiSelectProps>>([]);
  const [allEnterprise, setAllEnterpriseOption] = useState<Array<MultiSelectProps>>([]);
  const [allSubsidiaries, setAllSubsidiaries] = useState<Array<MultiSelectProps>>([]);

  const [showSchedulingGroupDropdown, setShowSchedulingGroupDropdown] = useState<boolean>(false);
  const [showSituationGroupDropdown, setShowSituationGroupDropdown] = useState<boolean>(false);
  const [showEnterpriseGroupDropdown, setShowEnterpriseGroupDropdown] = useState<boolean>(false);
  const [showSubsidiariesGroupDropdown, setSubsidiariesGroupDropdown] = useState<boolean>(false);

  const [countRequest, setCountRequest] = useState<number>(0);

  const [newData, setNewData] = useState('');
  const [loaderToSearchWithEnter, setLoaderToSearchWithEnter] = useState(false);

  const [isAdvancedFilter, setAdvancedFilter] = useState(false);

  const _domainSettingService = new DomainSettingService();
  const _subsidiaryService = new SubsidiaryService();
  const isMobile = isMobileDevice();

  let ticketReasonFilter = "";

  useEffect(() => {
    if (initialSearchFilter && initialSearchFilter !== searchStr) {
      setSearchStr(initialSearchFilter);
      setLoaderToSearchWithEnter(true);
    }
  }, [initialSearchFilter]);

  if (currentTabNamePage === OrdersTabsEnum.PEDIDO_FATURADO) {
    ticketReasonFilter = TicketReasonEnum.EXTRACTION_ORDERS;
  }

  if (currentTabNamePage === OrdersTabsEnum.VENDA_TRIANGULAR) {
    ticketReasonFilter = TicketReasonEnum.TRIANGULAR_SALES_EXTRACTION;
  }

  const fetchAllData = async () => {
    try {
      const fetches = [];

      if (viewFilters?.showSituationFilter) {
        fetches.push(fetchDomainSettingsTrackingTypes());
      }
      if (viewFilters?.showEnterpriseFilter) {
        fetches.push(fetchDomainSettingsEnterprises());
      }
      if (viewFilters?.showSubsidiaryFilter) {
        fetches.push(fetchSubsidiary());
      }

      await Promise.all(fetches).then((result) => {
        setCountRequest(result.length);
      });
    } catch (error) {
      console.log('Erro ao buscar dados:', error);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    filterIfRequestEqualNumberFilters();
  }, [allSituation, selectedSubsidiaries, selectedEnterprise]);

  useEffect(() => {
    filterIfRequestEqualNumberFilters();
  }, [loaderToSearchWithEnter]);

  const filterIfRequestEqualNumberFilters = () => {
    const viewFiltersCount = viewFilters ? Object.values(viewFilters).filter(x => x).length : 0;

    if (viewFiltersCount === countRequest || loaderToSearchWithEnter) {
      makeFilter();
    }
  };

  const makeFilter = (e?: FormEvent | KeyboardEvent) => {
    if (e) {
      e.preventDefault();
    }

    const newFilter = {
      textFilter: searchStr,
      startPeriod: period.from ? `${period.from}` : "",
      lastPeriod: period.to ? `${period.to}` : "",
      filterDate: selectDateType,
      status: selectedSituation?.length > 0 ? selectedSituation.toString() : "",
      enterpriseCode: returnIfEmployee(selectedEnterprise),
      subsidiary: returnIfEmployee(selectedSubsidiaries),
      orderType: currentTabPage,
      ticketReason: ticketReasonFilter,
      documentNumber: documentNumber
    };

    setCurrentPage(0);
    setTableFilter(newFilter);
    setIsLoadOrder(true);
    setLoaderToSearchWithEnter(false);
  };

  const returnIfEmployee = (valueSelected: string[]) => {
    if (user?.userType === EnumUserType.EMPLOYEE && valueSelected?.length > 0)
      return valueSelected.toString();

    return "";
  };

  const fetchDomainSettingsTrackingTypes = async () => {
    try {
      const result = await _domainSettingService.get(undefined, undefined, 'value asc', {
        and: [
          { identifier: { eq: 'GroupTrackingTypes' } },
          { enabled: { eq: true } },
        ],
      });

      if (result && result.data && result.data.length > 0) {
        const { value } = result.data[0];
        const situationArray = JSON.parse(value || '');

        const allObjSituationOption: Array<MultiSelectProps> = situationArray.map((item: SituationProps) => ({
          value: item.group,
          key: item.status,
        }));

        setAllSituationOption(allObjSituationOption);
      }
    } catch (error) {
      console.log('Erro ao obter as configurações do domínio:', error);
    }
  };

  const fetchDomainSettingsEnterprises = async () => {
    try {
      const result = await _domainSettingService.get(undefined, undefined, 'value asc', {
        and: [
          { identifier: { eq: 'EnterprisesViveo' } },
          { enabled: { eq: true } },
        ],
      });

      if (result && result.data && result.data.length > 0) {
        const { value } = result.data[0];
        const enterpriseArray = JSON.parse(value || '');

        const allEnterprise: Array<MultiSelectProps> = enterpriseArray.map((item: EnterpriseProps) => ({
          value: item.enterpriseName,
          key: item.enterpriseCode,
        }));

        setAllEnterpriseOption(allEnterprise);

        const uniqueStatuses: string[] = enterpriseArray.map((item: EnterpriseProps) => item.enterpriseCode);
        setSelectedEnterprise(uniqueStatuses);
      }
    } catch (error) {
      console.log('Erro ao obter as configurações do domínio:', error);
    }
  };

  const fetchSubsidiary = async () => {
    await _subsidiaryService
      .get(undefined, undefined, 'externalSubsidiaryCode asc, enterpriseName asc', {})
      .then((result: any) => {
        if (result && result.data) {

          const subsidiaries: Array<ISubsidiary> = result.data;
          let allSubsidiariesNew: Array<BUSelect> = [];

          subsidiaries.forEach((subsidiary: ISubsidiary) => {
            if (subsidiary.fullName && subsidiary.id) {
              const item = {
                value: `${subsidiary.externalSubsidiaryCode} - ${subsidiary.subsidiaryName}`,
                key: subsidiary.id,
              };

              allSubsidiariesNew.push({ ...allSubsidiariesNew, ...item });
            }
          });

          setAllSubsidiaries(allSubsidiariesNew);

          const subsidiariesSelectedConst: Array<string> = [];
          subsidiaries?.map(branch => {
            if (branch.id) {
              subsidiariesSelectedConst.push(branch.id);
            }
          });

          setSelectedSubsidiaries(subsidiariesSelectedConst);
        }
      });
  };

  const handleDateFirst = (e: ChangeEvent<HTMLInputElement>) => {
    setPeriod({ ...period, from: e.target.value });
    setNewData('end');
  };

  const handleSelectDateType = (value: string) => {
    setSelectDateType(value);
  };

  const handleAdvancedFiltersLoad = () => {
    setAdvancedFilter(!isAdvancedFilter);
  };

  const clearFilter = () => {
    setSearchStr('');
    setSelectedEnterprise([]);
    setSelectedSubsidiaries([]);
    setSelectedSituation([]);
    setSelectDateType("BillingDate");
    setCurrentPage(0);

    setPeriod({
      from: getMinDateInSearchFilter() ?
        DateOutputStr(getMinDateInSearchFilter().toISOString(), 'yyyy-MM-dd') : '',
      to: getMaxDateInSearchFilter() ?
        DateOutputStr(getMaxDateInSearchFilter().toISOString(), 'yyyy-MM-dd') : '',
    });

    const newFilter = {
      textFilter: "",
      startPeriod: DateOutputStr(getMinDateInSearchFilter().toISOString(), 'yyyy-MM-dd'),
      lastPeriod: DateOutputStr(getMaxDateInSearchFilter().toISOString(), 'yyyy-MM-dd'),
      filterDate: "BillingDate",
      status: "",
      enterpriseCode: "",
      subsidiary: "",
      orderType: currentTabPage,
      ticketReason: ticketReasonFilter
    };

    setTableFilter(newFilter);
  };

  const labelInputOrder = isMobile ? "Informe a ordem de compra, pedido ou NF" : "Filtro";
  const labelInputPeriod = isMobile ? "Selecione o período" : "Período";

  return (
    <>
      <Styled.TableSearchOrderContent
        id={randomTableSearchId}
        tabIndex={0}
        className={`${className || 'px-3 py-3'}`}
        isMobile={isMobile}
      >
        <Column className="mr-2 mr-md-2 w-md-100">
          <Text className="mb-1 color-blue-gray" size={12} weight="bold" testId="search-block-title">
            {labelInputOrder}
          </Text>

          <Row>
            <input
              onChange={(e) => setSearchStr(e.target.value)}
              type="text"
              placeholder={placeholder}
              value={searchStr}
              className="w-sm-100 w-md-100"
              name="searchStr"
              id="filter-input"
            />
          </Row>
        </Column>

        <Visibility visible={isMobile}>
          <Column className="mr-2 mr-md-2 w-md-100">
            <Text className="mb-1 color-blue-gray" size={12} weight="bold">
              CNPJ ou CPF do cliente
            </Text>
            <Row>
              <Input
                id="cnpj-cpf"
                data-testid="cnpj-cpf"
                stateName="documentNumber"
                className="w-100"
                value={formatDocument(documentNumber)}
                type="text"
                required
                name="CNPJ/CPF"
                onChange={(e) => setDocumentNumber(e.target.value)}
                maxLength={18}
              />
            </Row>
          </Column>

          <Visibility visible={viewFilters?.showSubsidiaryFilter || false}>
            <Column className="mr-2 mr-md-2 w-md-100">
              <Text className="mb-1 color-blue-gray" size={12} weight="bold">
                Selecione a filial
              </Text>
              <Row>
                <MultiSelectCustomized
                  itemsSelected={selectedSubsidiaries}
                  allItems={allSubsidiaries}
                  width="400px"
                  className="w-sm-100 w-md-100 status"
                  placeHolderOne="Selecione"
                  placeHolderMany="Selecionado"
                  style={{ minWidth: 140 }}
                  show={showSubsidiariesGroupDropdown}
                  setShow={setSubsidiariesGroupDropdown}
                />
              </Row>
            </Column>
          </Visibility>
        </Visibility>

        <Column className="mr-2 mr-md-2 w-md-100">
          <Text className="mr-2 mb-1 color-blue-gray" size={12} weight="bold" testId="search-block-period-label">
            {labelInputPeriod}
          </Text>
          <Row className="flex-1 align-center">
            <input
              className="flex-1"
              value={period.from}
              onChange={handleDateFirst}
              type="date"
              max={period.to}
              placeholder="Início"
              onBlur={() => calcDataTo(period, newData, setPeriod)}
              // onFocus={() => setOnFocusDateInput("date_first_order")}
              id="date_first_order"
            />
            <Text className="mx-1 color-blue-gray" size={12} weight="bold">
              a
            </Text>
            <PeriodDateToInput
              id="date_end_order"
              setNewData={setNewData}
              onBlur={() => calcDataFrom(period, newData, setPeriod)}
              className="flex-1"
              period={period}
              setPeriod={setPeriod}
              placeHolder={"Fim"}
            />
          </Row>
        </Column>

        <Visibility visible={showTypeDateFilter}>
          <Styled.PeriodColumn isMobile={isMobile}>
            <Styled.PeriodRow isMobile={isMobile}>
              <RadioInput
                id="BILLING_DATE"
                onClick={() => handleSelectDateType("BillingDate")}
                checked={selectDateType === 'BillingDate'}
              >
                <Text className="color-blue-gray">Data do faturamento</Text>
              </RadioInput>

              <RadioInput
                id="INVOICE_DELIVERY_DATE"
                onClick={() => handleSelectDateType("InvoiceDeliveryDate")}
                checked={selectDateType === 'InvoiceDeliveryDate'}
              >
                <Text className="color-blue-gray">Data de entrega</Text>
              </RadioInput>
            </Styled.PeriodRow>
          </Styled.PeriodColumn>
        </Visibility>

        <Visibility visible={false}>
          <Column className="mr-2 mr-md-2">
            <Text size={12} className="mt-md-4 mb-1 color-blue-gray mb-lg-1 mr-2">
              Agendamento
            </Text>
            <Row className="flex-1">
              <MultiSelectCustomized
                itemsSelected={["Pendente agendamento", "Pedidos agendados", "Pedidos sem agendamento"]}
                allItems={["Pendente agendamento", "Pedidos agendados", "Pedidos sem agendamento"]}
                width={"330px"}
                className="w-sm-100 w-md-100 status"
                placeHolderOne="Selecione"
                placeHolderMany="Selecionado"
                style={{ minWidth: 140 }}
                show={showSchedulingGroupDropdown}
                setShow={setShowSchedulingGroupDropdown}
              />
            </Row>
          </Column>
        </Visibility>

        <Visibility visible={!!(viewFilters?.showSituationFilter && !isMobile)}>
          <Column className="mr-2 mr-lg-0 mt-md-3 w-md-100">
            <Text className="mr-2 mb-1 color-blue-gray" size={12} weight="bold">
              Situação
            </Text>
            <Row className="flex-1">
              <MultiSelectCustomized
                itemsSelected={selectedSituation}
                allItems={allSituation}
                width="330px"
                className="w-sm-100 w-md-100 status"
                placeHolderOne="Selecione"
                placeHolderMany="Selecionado"
                style={{ minWidth: 140 }}
                show={showSituationGroupDropdown}
                setShow={setShowSituationGroupDropdown}
              />
            </Row>
          </Column>
        </Visibility>

        <Column className="mr-2 mt-md-3 w-md-100 pt-3 pt-md-0">
          <Row>
            <Styled.TableSearchOrderFocusBtn
              isMobile={isMobile}
              id="btn_table_search"
              tabIndex={0}
              onClick={(e) => makeFilter(e)}
            >
              <img src={IconSearch} alt="" />
            </Styled.TableSearchOrderFocusBtn>
          </Row>
        </Column>

        <SwitchComponent roles={['administrator', 'member']} type={['employee']}>
          <Visibility visible={showAdvancedFilters}>
            <Row className="justify-end justify-lg-end justify-md-center mt-2 mr-3">
              <Button onClick={() => handleAdvancedFiltersLoad()}>
                <Text size={14} className="color-primary mt-2">
                  <img
                    src={arrowFilter}
                    alt=""
                    style={{ width: 8, height: 9 }}
                    className={`${isAdvancedFilter && 'inverse-arrow'} mr-1`}
                  /> Filtro avançado
                </Text>
              </Button>
            </Row>
          </Visibility>
        </SwitchComponent>

        <Visibility visible={clearBtn}>
          <Row className="justify-end justify-lg-end justify-md-center mt-2 mr-3">
            <Button onClick={() => clearFilter()}>
              <Text size={14} className="color-primary mt-2">
                x Limpar filtros
              </Text>
            </Button>
          </Row>
        </Visibility>
      </Styled.TableSearchOrderContent>

      <Visibility visible={isAdvancedFilter}>
        <Row className="bg-white">
          <Row className="align-center flex-md-column align-md-none flex-wrap justify-md-none px-3 py-3 mx-3 mb-3">
            <Visibility visible={viewFilters?.showEnterpriseFilter || false}>
              <Column className="mr-2 mr-md-2 w-md-100">
                <Text className="mb-1 color-blue-gray" size={12} weight="bold">
                  Empresas
                </Text>
                <Row>
                  <MultiSelectCustomized
                    itemsSelected={selectedEnterprise}
                    allItems={allEnterprise}
                    width="330px"
                    className="w-sm-100 w-md-100"
                    placeHolderOne="Selecione"
                    placeHolderMany="Selecionado"
                    style={{ minWidth: 140 }}
                    show={showEnterpriseGroupDropdown}
                    setShow={setShowEnterpriseGroupDropdown}
                  />
                </Row>
              </Column>
            </Visibility>

            <Visibility visible={viewFilters?.showSubsidiaryFilter || false}>
              <Column className="mr-2 mr-md-2 w-md-100">
                <Text className="mb-1 color-blue-gray" size={12} weight="bold">
                  Filial
                </Text>
                <Row>
                  <MultiSelectCustomized
                    itemsSelected={selectedSubsidiaries}
                    allItems={allSubsidiaries}
                    width="400px"
                    className="w-sm-100 w-md-100 status"
                    placeHolderOne="Selecione"
                    placeHolderMany="Selecionado"
                    style={{ minWidth: 140 }}
                    show={showSubsidiariesGroupDropdown}
                    setShow={setSubsidiariesGroupDropdown}
                  />
                </Row>
              </Column>
            </Visibility>
          </Row>
        </Row>
      </Visibility>
    </>
  );
};

export default TableSearchOrder;